import { ANNOTATION_SESSION_ENDPOINT, AWS_LAMBDA_ENDPOINT } from "./constants";
import { v4 as uuidv4 } from "uuid";
import defaultSheet from "./defaultSheet.json";
import JSZip from 'jszip';
import { authFetch } from "./authFetch";

export const traverseItems = async (items) => {
  const files = [];

  const traverseEntry = async (entry, path = '') => {
    if (entry.isFile) {
      await new Promise((resolve) => {
        entry.file((file) => {
          files.push({ file, relativePath: path + file.name });
          resolve();
        });
      });
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const entries = [];
      let readEntries;
      do {
        readEntries = await new Promise((resolve) => reader.readEntries(resolve));
        entries.push(...readEntries);
      } while (readEntries.length > 0);
    
      for (const e of entries) {
        await traverseEntry(e, `${path}${entry.name}/`);
      }
    }
  };

  for (const item of items) {
    const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
    if (entry && entry.isDirectory) {
      await traverseEntry(entry);
    }
  }

  return files;
};

export const extractMidiFromZip = async (zipFile) => {
  const zip = await JSZip.loadAsync(zipFile);
  const midiFiles = [];

  const entries = Object.entries(zip.files);
  for (const [relativePath, zipEntry] of entries) {
    if (
      !zipEntry.dir &&
      /^(?!(\.|_)).*\.mid(i)?$/i.test(relativePath)
    ) {
      const fileData = await zipEntry.async('blob');
      const filename = zipEntry.name.split('/').pop();
      const file = new File([fileData], filename);
      midiFiles.push({ file, relativePath });
    }
  }

  return midiFiles;
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

async function createSheet(userIds, filename, filePath) {
  const sheetId = uuidv4();
  const sheetClone = JSON.parse(JSON.stringify(defaultSheet));

  sheetClone.data.general_info.original_filename = filename;
  sheetClone.data.general_info.displayed_filename = filename.replace(/\.[^/.]+$/, "");
  sheetClone.data.general_info.file_path = filePath;

  const res = await authFetch(
    `${AWS_LAMBDA_ENDPOINT}/createAnnotationSheet`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sheet_id: sheetId,
        user_ids: userIds,
        default_sheet: sheetClone
      })
    }
  );
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`CreateSheet failed (${res.status}): ${errText}`);
  }

  console.log(`✅ Created sheet ${sheetId} → "${filePath}"`);
  return sheetId;
}

export async function createSheetBatch({ userId, midiFiles, folder }) {
  return;
  const sheets = midiFiles.map(({ file, relativePath }) => {
    const sheetId = uuidv4();
    const sheetClone = JSON.parse(JSON.stringify(defaultSheet));
    const filePath = folder ? `${folder}/${relativePath}` : relativePath;

    sheetClone.data.general_info.original_filename = file.name;
    sheetClone.data.general_info.displayed_filename = file.name.replace(/\.[^/.]+$/, "");
    sheetClone.data.general_info.file_path = filePath;

    return {
      sheet_id: sheetId,
      default_sheet: sheetClone,
      file,
    };
  });

  const res = await authFetch(`${AWS_LAMBDA_ENDPOINT}/batchCreateAnnotationSheets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      sheets: sheets.map(({ sheet_id, default_sheet }) => ({
        sheet_id,
        default_sheet,
      })),
    }),
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Batch sheet creation failed (${res.status}): ${errText}`);
  }

  console.log(`✅ Created ${sheets.length} sheets`);

  return sheets.map(({ sheet_id, file }) => ({ sheetId: sheet_id, file }));
}

export const uploadMidi = async ({ file, sheetId, sessionId, onLoadingChange }) => {
  const originalFile = file;
  const processedFile = file;

  if (!originalFile || !processedFile || !sheetId) {
    alert("Missing required data for upload.");
    return;
  }

  try {
    if (onLoadingChange) onLoadingChange(true);

    const originalData = await readFileAsBase64(originalFile);
    const processedData = await readFileAsBase64(processedFile);

    const payload = [{
      sheet_id: sheetId,
      session_id: sessionId,
      original_filename: originalFile.name,
      original_data: originalData,
      processed_data: processedData,
    }];

    const res = await authFetch(`${ANNOTATION_SESSION_ENDPOINT}/uploadMidiFiles`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    // alert("Upload successful!");
    console.log(data);
  } catch (err) {
    console.error("Upload failed:", err);
    alert("Could not upload MIDI files.");
  } finally {
    if (onLoadingChange) onLoadingChange(false);
  }
};

export const uploadMidiFolder = async ({
  midiFiles,
  userId,
  folder,
  onLoadingChange,
}) => {
  if (midiFiles.length === 0) {
    alert("No MIDI files to upload.");
    return;
  }

  if (onLoadingChange) onLoadingChange(true);

  try {
    const sheetInfos = await createSheetBatch({ userId, midiFiles, folder });

    for (const { file, sheetId } of sheetInfos) {
      try {
        await uploadMidi({ file, sheetId });
      } catch (err) {
        console.error(`❌ Failed upload for ${file.name}: ${err.message}`);
      }
    }
  } catch (err) {
    console.error(`❌ Batch creation failed: ${err.message}`);
    alert("Failed to create annotation sheets.");
  } finally {
    if (onLoadingChange) onLoadingChange(false);
  }
};
